/**
 * Sim scroll-hijack guard.
 *
 * ## The problem
 * Every cursor-driven sim (`ProgramPoliciesSim`, the two Neobank sims, …) now
 * renders inside a fixed-height `overflow-hidden` frame (`SimFrame` / the
 * `framed` prop) and portals its Base UI `Select` (the policy-type dropdown)
 * INTO its own container. Opening/closing that Select mounts and unmounts a
 * large popup subtree inside the clipped frame, which yanks the WHOLE PAGE
 * toward the sim through two independent browser mechanisms:
 *
 *  1. **CSS scroll anchoring** (the dominant one, measured on the framed
 *     Policies sim). When the popup subtree appears/disappears, the browser's
 *     scroll-anchoring picks an anchor node and adjusts the document scroll to
 *     "keep content stable" — but the shifting content is inside a clipped
 *     fixed frame, so the adjustment scrolls the viewport to the sim. This is a
 *     native scroll: it goes through no JS scroll API and `preventScroll` on
 *     `focus()` does NOT stop it. Only disabling scroll anchoring does.
 *  2. **`scrollIntoView`** — on open, floating-ui-react's `useListNavigation`
 *     calls `option.scrollIntoView({ block: "nearest", inline: "nearest" })` to
 *     reveal the selected option. That's a LIBRARY call — not ours, and no
 *     Select/Positioner prop disables it (`alignItemWithTrigger` only affects
 *     positioning). Inside a frame that can't scroll locally it bubbles to the
 *     window.
 *
 * ## The fix
 * Sims are passive, auto-playing displays: nothing inside one should ever move
 * the viewport. Each cursor-driven sim marks its container with
 * `data-sim-no-scroll` (see `useSimCursor`) and portals its popup INTO that
 * same container, so every element the Select can anchor/scroll is a descendant
 * of a marked node. Two scoped defenses close both vectors:
 *
 *  - **CSS** — {@link SIM_NO_SCROLL_CSS} sets `overflow-anchor: none` on the
 *    marked subtree, so the browser never picks a scroll anchor inside a sim.
 *    Injected once by {@link installSimNoScrollGuard}. This is what actually
 *    stops the observed yank.
 *  - **JS** — a one-time patch of `Element.prototype.scrollIntoView` that
 *    no-ops for a marked-subtree target (covers the portaled popup too) and
 *    defers to native for everything else.
 *
 * Guarantees:
 * - **Scoped** — only sim descendants are affected; the rest of the app is
 *   untouched.
 * - **Idempotent** — module-level flag; installs once even if many sims mount.
 * - **SSR-safe** — no-ops when there's no DOM.
 */

const STYLE_ELEMENT_ID = "sim-no-scroll-guard";

/** Scoped scroll-anchoring kill-switch for sim subtrees (see module docs). */
export const SIM_NO_SCROLL_CSS = "[data-sim-no-scroll],[data-sim-no-scroll] *{overflow-anchor:none}";

let installed = false;

export function installSimNoScrollGuard(): void {
    if (installed) return;
    if (typeof document === "undefined") return; // SSR / non-DOM environment.
    installed = true;

    // 1. Kill scroll anchoring inside sim subtrees — the primary yank vector.
    if (!document.getElementById(STYLE_ELEMENT_ID)) {
        const style = document.createElement("style");
        style.id = STYLE_ELEMENT_ID;
        style.textContent = SIM_NO_SCROLL_CSS;
        document.head.appendChild(style);
    }

    // 2. Neutralize `scrollIntoView` for sim descendants (Base UI's open-scroll
    //    of the selected option). Covers popups portaled into a marked
    //    container; everything else keeps native behaviour.
    if (typeof Element === "undefined") return;
    const originalScrollIntoView = Element.prototype.scrollIntoView;
    Element.prototype.scrollIntoView = function patchedScrollIntoView(
        this: Element,
        ...args: Parameters<Element["scrollIntoView"]>
    ): void {
        if (this.closest?.("[data-sim-no-scroll]")) return;
        return originalScrollIntoView.apply(this, args);
    };
}
